import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { resizeImageFile } from "@/lib/resizeImage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Building2, MapPin, Palette, Banknote, Check, ChevronRight, ChevronLeft,
  Sparkles, Upload, X, BookOpen,
} from "lucide-react";
import ZefixLookup from "@/components/ZefixLookup";
import UidDuplicateBanner from "@/components/UidDuplicateBanner";
import { extractErrorMessage } from "@/lib/errorMessage";

const STEPS = [
  { id: 1, title: "Firma", icon: Building2 },
  { id: 2, title: "Adresse", icon: MapPin },
  { id: 3, title: "Branding", icon: Palette },
  { id: 4, title: "Bank & MWST", icon: Banknote },
  { id: 5, title: "Kontenplan", icon: BookOpen },
];

const CHART_OPTIONS = [
  {
    value: "kmu",
    title: "Schweizer KMU-Kontenrahmen",
    badge: "Empfohlen",
    desc: "~80 Konten nach Schweizer KMU-Standard (Käfer): Kasse, Bank, Debitoren, MWST, Lohn, Abschreibungen u.v.m.",
  },
  {
    value: "minimal",
    title: "Minimal-Kontenplan",
    badge: null,
    desc: "Die 20 wichtigsten Konten für Kleinstfirmen und Einzelunternehmen. Später jederzeit erweiterbar.",
  },
  {
    value: "none",
    title: "Leerer Kontenplan",
    badge: null,
    desc: "Keine Konten anlegen — selbst erfassen oder via Eröffnungsbilanz-Import (PDF) übernehmen.",
  },
];

const CH_VAT_RATES = [
  { value: 8.1, label: "8.1% — Normalsatz (Standard 2024+)" },
  { value: 2.6, label: "2.6% — Reduzierter Satz (Lebensmittel, Medikamente)" },
  { value: 3.8, label: "3.8% — Beherbergung" },
  { value: 0, label: "0% — Befreit / Export" },
];

const LEGAL_FORMS = ["GmbH", "AG", "Einzelfirma", "Verein", "Stiftung", "KlG", "KmG"];
// Rechtsformen, die unter CHF 500'000 Umsatz einfache Buchhaltung führen dürfen (OR 957 Abs. 2)
const SIMPLE_LEGAL_FORMS = ["Einzelfirma", "KlG", "KmG", "Verein"];
const MODE_OPTIONS = [
  { value: "simple", title: "Einfache Buchhaltung («Milchbüchlein»)", desc: "Einnahmen, Ausgaben und Vermögenslage – ohne Bilanz-Fachbegriffe. Erlaubt für Einzelfirmen und Personengesellschaften unter CHF 500'000 Umsatz. Jederzeit umstellbar." },
  { value: "double", title: "Doppelte Buchhaltung", desc: "Bilanz und Erfolgsrechnung nach OR 957 – Pflicht für AG, GmbH und ab CHF 500'000 Umsatz. Alle Funktionen sichtbar." },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", uid: "", vat_number: "", legal_form: "GmbH",
    street: "", postal_code: "", city: "", country: "CH",
    phone: "", email: "",
    logo_data_url: "", primary_color: "#0f172a", accent_color: "#10b981", invoice_footer: "",
    iban: "", default_bank_account_id: "1020", default_vat_rate: 8.1, currency: "CHF",
    chart_of_accounts: "kmu",
    accounting_mode: "double",
  });
  const simpleEligible = SIMPLE_LEGAL_FORMS.includes(form.legal_form);

  // Status-Check beim Mount
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.get("/onboarding/status");
        if (!res.data.needs_onboarding) {
          // Hat schon eine echte Firma → zum Dashboard
          navigate("/");
        }
      } catch (e) { /* fail-soft: zeig Wizard */ }
    })();
  }, [navigate]);

  const updateField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { dataUrl, width, height, resized } = await resizeImageFile(file);
      updateField("logo_data_url", dataUrl);
      if (resized) toast.success(`Logo automatisch auf ${width}×${height} px verkleinert (Seitenverhältnis unverändert)`);
    } catch {
      toast.error("Logo konnte nicht gelesen werden");
    }
  };

  const canProceed = () => {
    if (step === 1) return form.name.trim().length >= 2;
    return true;
  };

  const handleNext = () => {
    if (!canProceed()) {
      toast.error("Bitte fülle die Pflichtfelder aus.");
      return;
    }
    if (step < STEPS.length) setStep(step + 1);
  };

  const handleBack = () => step > 1 && setStep(step - 1);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Firmenname fehlt.");
      setStep(1);
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post("/onboarding/complete", form);
      if (res.data.already_completed) {
        toast.info("Onboarding war schon abgeschlossen.");
      } else {
        toast.success(`Willkommen ${form.name}! Deine Firma ist bereit.`);
      }
      // Set company-id für die App
      localStorage.setItem("demoCompanyId", res.data.company_id);
      navigate("/");
    } catch (e) {
      toast.error(extractErrorMessage(e, "Onboarding fehlgeschlagen."));
    } finally {
      setSubmitting(false);
    }
  };

  const StepIcon = STEPS[step - 1].icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30 py-8 px-4" data-testid="onboarding-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium mb-3">
            <Sparkles className="w-3.5 h-3.5" />
            Setup-Wizard
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Willkommen!</h1>
          <p className="text-slate-600 mt-1">
            In 5 kurzen Schritten ist Deine Buchhaltung einsatzbereit.
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-between mb-6 px-2" data-testid="onboarding-progress">
          {STEPS.map((s, idx) => {
            const Icon = s.icon;
            const isActive = s.id === step;
            const isDone = s.id < step;
            return (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center gap-1 flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      isActive ? "bg-emerald-600 text-white shadow-lg scale-110" :
                      isDone ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                    }`}
                    data-testid={`onboarding-step-${s.id}-indicator`}
                  >
                    {isDone ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`text-xs font-medium ${isActive ? "text-emerald-700" : "text-slate-500"}`}>
                    {s.title}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 ${isDone ? "bg-emerald-300" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <Card className="p-6 shadow-md border border-slate-200">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white">
              <StepIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-slate-500">Schritt {step} von {STEPS.length}</div>
              <h2 className="text-lg font-semibold text-slate-900">{STEPS[step - 1].title}</h2>
            </div>
          </div>

          {/* Step 1: Firma */}
          {step === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-1">
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-indigo-900">
                  Firma im Schweizer Handelsregister suchen (empfohlen)
                </Label>
                <ZefixLookup
                  onSelect={(r) => setForm((prev) => ({
                    ...prev,
                    name: r.name || prev.name,
                    uid: r.uid_formatted || r.uid || prev.uid,
                    legal_form: r.legal_form || prev.legal_form,
                    street: r.street || prev.street,
                    postal_code: r.postal_code || prev.postal_code,
                    city: r.city || prev.city,
                  }))}
                  placeholder="z.B. Migros, Swisscom, Coop…"
                />
                <p className="text-xs text-slate-600">
                  Firmenname, UID, Rechtsform, Straße, PLZ & Ort werden automatisch übernommen (Zefix + BFS UID-Register).
                </p>
              </div>
              <UidDuplicateBanner uid={form.uid} />
              <div>
                <Label htmlFor="ob-name">Firmenname *</Label>
                <Input
                  id="ob-name"
                  value={form.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  placeholder="Musterfirma GmbH"
                  data-testid="onboarding-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ob-legal">Rechtsform</Label>
                  <select
                    id="ob-legal"
                    className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm"
                    value={form.legal_form}
                    onChange={(e) => { const lf = e.target.value; setForm((prev) => ({ ...prev, legal_form: lf, accounting_mode: SIMPLE_LEGAL_FORMS.includes(lf) ? "simple" : "double", chart_of_accounts: SIMPLE_LEGAL_FORMS.includes(lf) ? "minimal" : prev.chart_of_accounts })); }}
                    data-testid="onboarding-legal-form-select"
                  >
                    {LEGAL_FORMS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="ob-uid">UID-Nr (optional)</Label>
                  <Input
                    id="ob-uid"
                    value={form.uid}
                    onChange={(e) => updateField("uid", e.target.value)}
                    placeholder="CHE-123.456.789"
                    data-testid="onboarding-uid-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="ob-vat">MWST-Nr (optional)</Label>
                <Input
                  id="ob-vat"
                  value={form.vat_number}
                  onChange={(e) => updateField("vat_number", e.target.value)}
                  placeholder="CHE-123.456.789 MWST"
                  data-testid="onboarding-vat-number-input"
                />
              </div>
            </div>
          )}

          {/* Step 2: Adresse */}
          {step === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-2">
              <div>
                <Label htmlFor="ob-street">Strasse + Nr</Label>
                <Input
                  id="ob-street"
                  value={form.street}
                  onChange={(e) => updateField("street", e.target.value)}
                  placeholder="Bahnhofstrasse 12"
                  data-testid="onboarding-street-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <Label htmlFor="ob-plz">PLZ</Label>
                  <Input
                    id="ob-plz"
                    value={form.postal_code}
                    onChange={(e) => updateField("postal_code", e.target.value)}
                    placeholder="8000"
                    data-testid="onboarding-postal-code-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="ob-city">Ort</Label>
                  <Input
                    id="ob-city"
                    value={form.city}
                    onChange={(e) => updateField("city", e.target.value)}
                    placeholder="Zürich"
                    data-testid="onboarding-city-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ob-phone">Telefon</Label>
                  <Input
                    id="ob-phone"
                    value={form.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    placeholder="+41 44 123 45 67"
                    data-testid="onboarding-phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ob-email">E-Mail</Label>
                  <Input
                    id="ob-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="info@firma.ch"
                    data-testid="onboarding-email-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Branding */}
          {step === 3 && (
            <div className="space-y-4" data-testid="onboarding-step-3">
              <div>
                <Label>Firmenlogo (optional, PNG/JPEG, max. 500 KB)</Label>
                <div className="flex items-center gap-3 mt-1">
                  <div
                    className="w-24 h-16 rounded border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50"
                    data-testid="onboarding-logo-preview"
                  >
                    {form.logo_data_url ? (
                      <img src={form.logo_data_url} alt="Vorschau" className="max-h-14 max-w-20 object-contain" />
                    ) : (
                      <span className="text-xs text-slate-400">Kein Logo</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml"
                      onChange={handleLogoUpload}
                      className="text-xs"
                      data-testid="onboarding-logo-upload"
                    />
                    {form.logo_data_url && (
                      <button
                        type="button"
                        onClick={() => updateField("logo_data_url", "")}
                        className="text-xs text-rose-600 hover:underline mt-1 flex items-center gap-1"
                        data-testid="onboarding-logo-remove"
                      >
                        <X className="w-3 h-3" /> Entfernen
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ob-primary">Primärfarbe</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={form.primary_color}
                      onChange={(e) => updateField("primary_color", e.target.value)}
                      className="h-9 w-12 rounded border border-slate-200 cursor-pointer"
                      data-testid="onboarding-primary-color-input"
                    />
                    <Input
                      value={form.primary_color}
                      onChange={(e) => updateField("primary_color", e.target.value)}
                      className="flex-1 font-mono text-xs"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="ob-accent">Akzentfarbe</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={form.accent_color}
                      onChange={(e) => updateField("accent_color", e.target.value)}
                      className="h-9 w-12 rounded border border-slate-200 cursor-pointer"
                      data-testid="onboarding-accent-color-input"
                    />
                    <Input
                      value={form.accent_color}
                      onChange={(e) => updateField("accent_color", e.target.value)}
                      className="flex-1 font-mono text-xs"
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="ob-footer">Rechnungs-Fußzeile (optional)</Label>
                <textarea
                  id="ob-footer"
                  rows={3}
                  value={form.invoice_footer}
                  onChange={(e) => updateField("invoice_footer", e.target.value)}
                  placeholder="Vielen Dank · IBAN CH00... · Zahlbar innert 30 Tagen"
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-y"
                  data-testid="onboarding-footer-input"
                />
              </div>
            </div>
          )}

          {/* Step 4: Bank + MWST */}
          {step === 4 && (
            <div className="space-y-4" data-testid="onboarding-step-4">
              <div>
                <Label htmlFor="ob-iban">IBAN (für QR-Rechnungen)</Label>
                <Input
                  id="ob-iban"
                  value={form.iban}
                  onChange={(e) => updateField("iban", e.target.value)}
                  placeholder="CH00 0000 0000 0000 0000 0"
                  className="font-mono"
                  data-testid="onboarding-iban-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Wird für Swiss-QR-Bills auf Rechnungen verwendet.
                </p>
              </div>
              <div>
                <Label htmlFor="ob-vat-rate">Standard-MWST-Satz</Label>
                <select
                  id="ob-vat-rate"
                  className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm"
                  value={form.default_vat_rate}
                  onChange={(e) => updateField("default_vat_rate", parseFloat(e.target.value))}
                  data-testid="onboarding-vat-rate-select"
                >
                  {CH_VAT_RATES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-800">
                <div className="font-semibold mb-1">🎉 Fast geschafft!</div>
                Nach dem Klick auf &quot;Fertig&quot; wird Deine Firma angelegt — Kontenplan,
                Standardkonten und MWST-Setup werden automatisch initialisiert.
                Du kannst alles später in den Einstellungen anpassen.
              </div>
            </div>
          )}

          {/* Step 5: Kontenplan */}
          {step === 5 && (
            <div className="space-y-3" data-testid="onboarding-step-5">
              <p className="text-sm text-slate-600">Wie möchtest Du Deine Buchhaltung führen?</p>
              {simpleEligible && (
                <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="onboarding-mode-suggestion">
                  Für die Rechtsform <b>{form.legal_form}</b> genügt unter CHF 500'000 Umsatz die einfache Buchhaltung – wir schlagen das Milchbüchlein vor.
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-2">
                {MODE_OPTIONS.map((opt) => {
                  const isSelected = form.accounting_mode === opt.value;
                  return (
                    <button key={opt.value} type="button" onClick={() => updateField("accounting_mode", opt.value)}
                      className={`text-left rounded-lg border p-3 transition-all ${isSelected ? "border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500" : "border-slate-200 bg-white hover:border-slate-300"}`}
                      data-testid={`onboarding-mode-${opt.value}`}>
                      <div className="text-sm font-medium text-slate-900">{opt.title}</div>
                      <div className="text-xs text-slate-500 mt-1">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
              <p className="text-sm text-slate-600 pt-2">
                Mit welchem Kontenplan möchtest Du starten?
              </p>
              {CHART_OPTIONS.map((opt) => {
                const isSelected = form.chart_of_accounts === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateField("chart_of_accounts", opt.value)}
                    className={`w-full text-left rounded-lg border p-3 transition-all ${
                      isSelected
                        ? "border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    data-testid={`onboarding-chart-option-${opt.value}`}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                          isSelected ? "border-emerald-600 bg-emerald-600" : "border-slate-300"
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm font-semibold text-slate-900">{opt.title}</span>
                      {opt.badge && (
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] border">
                          {opt.badge}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-6">{opt.desc}</p>
                  </button>
                );
              })}
            </div>
          )}

          {/* Navigation */}
          <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 1 || submitting}
              data-testid="onboarding-back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
            </Button>
            {step < STEPS.length ? (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="onboarding-next-btn"
              >
                Weiter <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="onboarding-finish-btn"
              >
                {submitting ? "Lege Firma an…" : (
                  <>
                    <Check className="w-4 h-4 mr-1" /> Fertig & Loslegen
                  </>
                )}
              </Button>
            )}
          </div>
        </Card>

        {/* Skip-Hinweis */}
        <div className="mt-4 text-center">
          <button
            onClick={() => navigate("/")}
            className="text-xs text-slate-400 hover:text-slate-600"
            data-testid="onboarding-skip-link"
          >
            Später (zur Demo-Firma)
          </button>
        </div>
      </div>
    </div>
  );
}
